import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  Typography,
  Avatar,
  Stack,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useQuery } from "react-query";
import { getClinicianProfile } from "../../../api/services/api";
import {
  StyledDrawerContent,
  StyledDrawerHeader,
} from "../../../components/DrawerTemplate";
import { EditProfileForm } from "./EditProfileForm";
import { SideProfileInfo } from "./SideProfileInfo";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WcIcon from "@mui/icons-material/Wc";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import LanguageIcon from "@mui/icons-material/Language";
import EditIcon from "@mui/icons-material/Edit";

export const BasicInformation = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const toggleDrawer = (newOpen: any) => {
    setOpen(newOpen);
  };

  const { data, isLoading } = useQuery(
    ["clinician-profile"],
    getClinicianProfile
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  let item = data?.data;
  let Gender = "N/A";
  if (item && typeof item.gender === 'string' && item.gender.length > 0) {
    Gender =
      item.gender.slice(0, 1).toUpperCase() +
      item.gender.slice(1).toLowerCase();
  }

  const languages = item?.languagesKnown?.split(',') || [];

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Profile Header Card */}
        <Grid item xs={12}>
          <Card sx={{ background: `linear-gradient(135deg, ${theme.colors.primary.lighter} 0%, ${theme.colors.info.lighter} 100%)` }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={3}>
                <Avatar
                  src={item?.imageUrl}
                  sx={{
                    width: 120,
                    height: 120,
                    border: `4px solid white`,
                    boxShadow: 3,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 60 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {item?.users?.fullName || "N/A"}
                  </Typography>
                  <Typography variant="h6" sx={{ color: theme.colors.primary.main, mb: 1 }}>
                    {item?.designation || "N/A"}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip
                      icon={<LocationOnIcon />}
                      label={item?.location || "N/A"}
                      size="small"
                      sx={{ backgroundColor: "white" }}
                    />
                    <Chip
                      icon={<WorkIcon />}
                      label={`${item?.experience || 0} years experience`}
                      size="small"
                      sx={{ backgroundColor: "white" }}
                    />
                  </Stack>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setOpen(true)}
                  sx={{
                    backgroundColor: "white",
                    color: theme.colors.primary.main,
                    "&:hover": {
                      backgroundColor: theme.colors.alpha.white[70],
                    },
                  }}
                >
                  Edit Profile
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Personal Information */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%", boxShadow: 2 }}>
            <Box p={3}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Personal Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <InfoItem
                    icon={<PersonIcon sx={{ color: theme.colors.primary.main }} />}
                    label="Full Name"
                    value={item?.users?.fullName}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem
                    icon={<WcIcon sx={{ color: theme.colors.info.main }} />}
                    label="Gender"
                    value={Gender}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem
                    icon={<CakeIcon sx={{ color: theme.colors.warning.main }} />}
                    label="Date of Birth"
                    value={moment(item?.dob).format("DD MMM YYYY")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem
                    icon={<PhoneIcon sx={{ color: theme.colors.success.main }} />}
                    label="Mobile Number"
                    value={item?.users?.mobileNumber}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoItem
                    icon={<EmailIcon sx={{ color: theme.colors.error.main }} />}
                    label="Email Address"
                    value={item?.users?.email}
                  />
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Grid>

        {/* Professional Information */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%", boxShadow: 2 }}>
            <Box p={3}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                Professional Details
              </Typography>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70], fontWeight: 600 }}>
                    Designation
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {item?.designation || "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70], fontWeight: 600 }}>
                    Experience
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {item?.experience || "0"} years
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70], fontWeight: 600 }}>
                    License Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                    {item?.license || "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: theme.colors.alpha.black[70], fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <LanguageIcon sx={{ fontSize: 16 }} />
                    Languages Known
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                    {languages.length > 0 ? (
                      languages.map((lang: string, index: number) => (
                        <Chip
                          key={index}
                          label={lang.trim()}
                          size="small"
                          sx={{
                            backgroundColor: theme.colors.primary.lighter,
                            color: theme.colors.primary.main,
                            fontWeight: 600,
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">N/A</Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Grid>

        {/* Bio Section */}
        <Grid item xs={12}>
          <Card sx={{ boxShadow: 2 }}>
            <Box p={3}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                Professional Bio
              </Typography>
              <Typography variant="body1" sx={{ color: theme.colors.alpha.black[70], lineHeight: 1.8 }}>
                {item?.bio || "No bio available"}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Drawer anchor="right" open={open} onClose={() => toggleDrawer(false)}>
        <StyledDrawerHeader>
          <Typography>Edit Profile</Typography>
          <Typography
            sx={{
              fontSize: "12px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => toggleDrawer(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          {item && (
            <EditProfileForm
              prevData={item}
              toggleDrawer={(e: any) => toggleDrawer(e)}
            />
          )}
        </StyledDrawerContent>
      </Drawer>
    </Box>
  );
};

const InfoItem = ({ icon, label, value }: any) => {
  return (
    <Box display="flex" gap={1.5}>
      <Box sx={{ mt: 0.5 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
          {value || "N/A"}
        </Typography>
      </Box>
    </Box>
  );
};
